import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ReviewTag {
  id: string;
  review_id: string;
  tag: string;
  created_at: string;
}

export const useReviewTags = (reviewId?: string) => {
  return useQuery({
    queryKey: ['review-tags', reviewId],
    queryFn: async () => {
      if (!reviewId) return [];
      
      const { data, error } = await supabase
        .from('review_tags')
        .select('*')
        .eq('review_id', reviewId)
        .order('created_at');

      if (error) throw error;
      return data as ReviewTag[];
    },
    enabled: !!reviewId,
  });
};

export const useAddReviewTag = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, tag }: { reviewId: string; tag: string }) => {
      const { data, error } = await supabase
        .from('review_tags')
        .insert({
          review_id: reviewId,
          tag: tag.toLowerCase().trim(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['review-tags', variables.reviewId] });
      toast({
        title: 'Tag added',
        description: 'Tag has been added to your review.',
      });
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate')) {
        toast({
          title: 'Tag already exists',
          description: 'This tag is already on this review.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Failed to add tag',
          description: error.message,
          variant: 'destructive',
        });
      }
    },
  });
};

export const useRemoveReviewTag = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tagId, reviewId }: { tagId: string; reviewId: string }) => {
      const { error } = await supabase
        .from('review_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
      return { reviewId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['review-tags', data.reviewId] });
      toast({
        title: 'Tag removed',
        description: 'Tag has been removed from your review.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove tag',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Get popular tags across all reviews
export const usePopularTags = (limit = 20) => {
  return useQuery({
    queryKey: ['popular-tags', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('review_tags')
        .select('tag');

      if (error) throw error;

      // Count tag occurrences
      const tagCounts = (data || []).reduce((acc, { tag }) => {
        acc[tag] = (acc[tag] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Sort by count and return top tags
      return Object.entries(tagCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([tag, count]) => ({ tag, count }));
    },
  });
};